<script>
import { mapGetters } from 'vuex'
import { BPopover } from 'bootstrap-vue/esm/components/popover'
import IconMinus from '@/components/atoms/IconMinus.vue'
import IconPlus from '@/components/atoms/IconPlus.vue'

export default {
    name: 'InputStep',
    components: { BPopover, IconMinus, IconPlus },
    props: {
        value: {
            type: Number,
            default: 0,
        },
        disabled: Boolean,
        readonly: Boolean,
        disableAdd: Boolean,
        removable: Boolean,
        xxs: Boolean,
        xs: Boolean,
        sm: Boolean,
        md: Boolean,
        lg: Boolean,
        variant: Boolean,
        dynamic: Boolean,
        max: {
            type: Number,
            default: undefined,
        },
        min: {
            type: Number,
            default: 0,
        },
        popoverContainer: {
            type: [String, HTMLElement],
            default: '#app',
        },
    },
    data() {
        return {
            innerValue: 0,
            hintText: null,
            parent: null,
            focus: false,
        }
    },
    computed: {
        ...mapGetters(['smDevice']),
        iconSize() {
            if (this.xxs) return 'xs'
            if (this.xs) return 'sm'
            if (this.lg) return 'lg'
            return undefined
        },
        trashIconSize() {
            if (this.xs) return 'sm'
            if (this.lg) return 'lg'
            return 'sm'
        },
    },
    watch: {
        value: {
            immediate: true,
            handler(val) {
                this.$set(this, 'innerValue', val)
            },
        },
    },
    methods: {
        inc() {
            if (!this.readonly) {
                if (!this.disable && (!this.max || this.innerValue < this.max)) this.innerValue += 1
                this.emit()
            }
        },
        dec() {
            if (!this.readonly) {
                if (!this.disable && this.innerValue > this.min) this.innerValue -= 1
                this.emit()
            }
        },
        emit() {
            this.$emit('input', this.innerValue)
        },
        onFocus() {
            this.focus = true
        },
        onBlur(e) {
            this.focus = false
            this.setValue(e)
        },
        onKeypress(e) {
            if (isNaN(String.fromCharCode(e.which))) e.preventDefault()
        },
        setValue({ type, target: { textContent } }) {
            if (type === 'keyup') {
                this.$refs.value.blur()
            } else {
                const value = parseInt(textContent)
                if (
                    (isNaN(value) ||
                        (isNaN(this.min) ? false : value < this.min) ||
                        (isNaN(this.max) ? false : value > this.max)) &&
                    !(value === 0 && this.removable)
                ) {
                    if (isNaN(value)) {
                        this.innerValue = this.removable ? 0 : this.min
                    } else if (value < this.min) {
                        this.innerValue = this.min
                    } else if (value > this.max) {
                        this.innerValue = this.max
                        this.setTooltip(`${this.$t('meal.maximum_quantity')} ${this.max}`)
                    }
                } else {
                    this.innerValue = parseInt(value)
                }
                this.$refs.value.textContent = this.innerValue.toString()
                this.emit()
            }
        },
        setTooltip(hintText) {
            this.hintText = hintText
            setTimeout(() => {
                this.hintText = null
            }, 3000)
        },
    },
}
</script>

<template lang="pug">
.form-group.d-inline-flex.align-items-center(ref="inputStep")
    transition(name="fade")
        b-popover(
            :target="`inputStep-${_uid}`" 
            :show="!!hintText" 
            triggers="manual"
            placement="auto"
            :key="`inputStep-${popoverContainer}`" 
            :container="popoverContainer") {{ hintText }}
    label(v-if="$slots.default")
        slot
    .input-step(:id="`inputStep-${_uid}`" :class="{'step-variant': variant, 'input-step-xxs': xxs, 'input-step-xs': xs, 'input-step-sm': sm, 'input-step-md': md, 'input-step-lg': lg, 'highlight': !!hintText}")
        template(v-if="!variant || (variant && !dynamic) || (innerValue > min)")
            button.btn-reset.btn-step.rounded-circle(
                @click.stop="dec"
                :class="[variant ? (disabled || innerValue <= min ? 'text-cc' : 'text-73') : (disabled || innerValue <= min ? 'bg-cc' : 'bg-white')]"
                :disabled="disabled || innerValue <= min")
                transition(name="fade" mode="out-in")
                    icon(v-if="innerValue <= min + 1 && removable && !variant" icon="fa-solid fa-trash" :size="trashIconSize")
                    icon-minus(v-else-if="variant" :size="iconSize")
                    icon(v-else icon="fa-solid fa-minus" :size="iconSize")
            .input-step-value(:class="[variant ? 'mx-3' : 'mx-2', {'text-cc': disabled, 'bg-ef': variant,  'in-focus': focus}]")
                span(
                v-text="innerValue"
                ref="value"
                @keypress="onKeypress" 
                @keyup.enter="setValue" 
                @blur="onBlur" 
                @focus="onFocus" 
                contenteditable)
                icon(v-if="!variant" icon="fa-solid fa-xmark" size="xs") 
            //- .input-step-value.mx-2(:class="{'text-cc': disabled}" ref="value") {{innerValue}}
        button.btn-reset.btn-step.rounded-circle(
            @click.stop="inc"
            :class="[variant ? ((disabled || (max && innerValue >= max)) || disableAdd ? 'text-cc' : 'text-73') : ((disabled || (max && innerValue >= max)) || disableAdd ? 'bg-cc' : 'bg-white')]"
            :disabled="(disabled || (max && innerValue >= max)) || disableAdd")
            icon-plus(v-if="variant")
            icon(v-else icon="fa-solid fa-plus" :size="iconSize")
</template>
